import { useEffect, type ReactNode } from 'react';
import { FctIcono } from './marca';
import type { FctNombreIcono } from '../iconos';

/* ===========================================================================
 * Capa semántica: cinco dimensiones, cinco canales. El color nunca va solo:
 * color + icono + etiqueta, siempre.
 * ========================================================================= */

export type FctFamilia = 'exito' | 'aviso' | 'riesgo' | 'proceso' | 'neutro';

/* ---------------------------------------------------------------------------
 * Estado — siete valores. Una app puede usar un subconjunto, nunca un octavo
 * con color propio. Los nombres de dominio se mapean con `personalizado`.
 * ------------------------------------------------------------------------- */
export type FctValorEstado = 'borrador' | 'en-curso' | 'en-espera' | 'bloqueado' | 'completado' | 'cancelado' | 'archivado';

export const ESTADOS: Record<FctValorEstado, { etiqueta: string; familia: FctFamilia; icono: FctNombreIcono }> = {
  borrador: { etiqueta: 'Borrador', familia: 'neutro', icono: 'editar' },
  'en-curso': { etiqueta: 'En curso', familia: 'proceso', icono: 'transmision' },
  'en-espera': { etiqueta: 'En espera', familia: 'aviso', icono: 'hora' },
  bloqueado: { etiqueta: 'Bloqueado', familia: 'riesgo', icono: 'alerta' },
  completado: { etiqueta: 'Completado', familia: 'exito', icono: 'completado' },
  cancelado: { etiqueta: 'Cancelado', familia: 'neutro', icono: 'cancelado' },
  archivado: { etiqueta: 'Archivado', familia: 'neutro', icono: 'carpeta' },
};

const ICONO_FAMILIA: Record<FctFamilia, FctNombreIcono> = {
  exito: 'completado', aviso: 'hora', riesgo: 'alerta', proceso: 'transmision', neutro: 'punto',
};

export interface FctEstadoProps {
  estado?: FctValorEstado;
  /** Para estados de dominio («Firmado», «Vinculada»): se mapean a una familia. */
  personalizado?: { etiqueta: string; familia: FctFamilia; icono?: FctNombreIcono };
}

export function FctEstado({ estado, personalizado }: FctEstadoProps) {
  const e = personalizado
    ? { ...personalizado, icono: personalizado.icono ?? ICONO_FAMILIA[personalizado.familia] }
    : ESTADOS[estado ?? 'borrador'];
  return (
    <span className={`fct-estado fct-estado--${e.familia}`}>
      <FctIcono nombre={e.icono} />
      {e.etiqueta}
    </span>
  );
}

/**
 * Migración desde «Pendiente / Realizada / Descartada» (GCvsc):
 * con incidencia de bloqueo (E1, E2) activa → bloqueado; si no → en-espera.
 */
export function migrarEstadoAntiguo(antiguo: string, incidencias: string[] = []): FctValorEstado {
  const a = antiguo.trim().toLowerCase();
  if (a.startsWith('realizad')) return 'completado';
  if (a.startsWith('descartad')) return 'cancelado';
  if (a.startsWith('pendiente')) return incidencias.some((c) => /^E[12]$/i.test(c)) ? 'bloqueado' : 'en-espera';
  return 'borrador';
}

/* ---------------------------------------------------------------------------
 * Atención — cuatro niveles. Filete al borde de la FILA (nunca en tarjeta) + glifo.
 * Las incidencias del motor de reglas no tienen color: son la causa que sube el nivel.
 * ------------------------------------------------------------------------- */
export type FctNivelAtencion = 'normal' | 'info' | 'atencion' | 'urgente';

const GLIFO: Record<Exclude<FctNivelAtencion, 'normal'>, { g: string; t: string }> = {
  urgente: { g: '▲', t: 'Urgente' },
  atencion: { g: '△', t: 'Atención' },
  info: { g: 'i', t: 'Informativo' },
};

export function FctGlifoAtencion({ nivel }: { nivel: FctNivelAtencion }) {
  if (nivel === 'normal') return <span className="fct-glifo" aria-hidden="true" />;
  return (
    <span className={`fct-glifo fct-glifo--${nivel}`} role="img" aria-label={GLIFO[nivel].t}>
      {GLIFO[nivel].g}
    </span>
  );
}

/** Clase para la <tr> (tabla propia o rowClassName de Ant Design). */
export function claseFilaAtencion(nivel: FctNivelAtencion): string {
  return nivel === 'urgente' ? 'fct-fila--urgente' : nivel === 'atencion' ? 'fct-fila--atencion' : '';
}

/** Nivel desde códigos de incidencia: E1/E2 → urgente, I1/I2 → atención, F7 → info. */
export function nivelDesdeIncidencias(codigos: string[]): FctNivelAtencion {
  if (codigos.some((c) => /^E[12]$/i.test(c))) return 'urgente';
  if (codigos.some((c) => /^I\d/i.test(c))) return 'atencion';
  if (codigos.length > 0) return 'info';
  return 'normal';
}

/* ---------------------------------------------------------------------------
 * Probabilidad y escalas ordenadas: rampa de tres puntos en verde oscuro + etiqueta.
 * No usa el semáforo, para no confundirse con el estado.
 * ------------------------------------------------------------------------- */
export interface FctProbabilidadProps {
  nivel: 1 | 2 | 3;
  etiqueta?: string;
  /** Porcentaje opcional (DIANA): se muestra en Mono al lado. */
  porcentaje?: number;
}

const ETIQUETA_PROB = { 1: 'En progreso', 2: 'Probable', 3: 'Altamente probable' } as const;

export function FctProbabilidad({ nivel, etiqueta, porcentaje }: FctProbabilidadProps) {
  return (
    <span className="fct-prob">
      <span className="fct-prob__puntos" aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <i key={n} className={n <= nivel ? 'fct-lleno' : undefined} />
        ))}
      </span>
      {etiqueta ?? ETIQUETA_PROB[nivel]}
      {porcentaje !== undefined && <span className="fct-mono">{Math.round(porcentaje)} %</span>}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Barra en celda — cantidad o antigüedad dentro de una celda. Sustituye al mapa de calor.
 * ------------------------------------------------------------------------- */
export interface FctBarraCeldaProps {
  valor: number;
  max: number;
  /** Texto a mostrar (por defecto, el valor). */
  texto?: string;
  nivel?: 'normal' | 'atencion' | 'urgente';
}

export function FctBarraCelda({ valor, max, texto, nivel = 'normal' }: FctBarraCeldaProps) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (valor / max) * 100)) : 0;
  return (
    <span className={`fct-barra-celda${nivel !== 'normal' ? ` fct-barra-celda--${nivel}` : ''}`}>
      <span className="fct-mono">
        {nivel !== 'normal' && <FctGlifoAtencion nivel={nivel} />}
        {texto ?? valor.toLocaleString('es-ES')}
      </span>
      <span className="fct-barra-celda__pista" aria-hidden="true">
        <i style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Línea de vida — fase en una sola tinta. Ningún hito tiene su color.
 * ------------------------------------------------------------------------- */
export type FctEstadoPaso = 'hecho' | 'actual' | 'pendiente' | 'omitido' | 'fallido';

export function FctLineaVida({ pasos }: { pasos: { nombre: string; estado: FctEstadoPaso }[] }) {
  return (
    <ol className="fct-vida">
      {pasos.map((p) => (
        <li key={p.nombre} className={`fct-vida__paso fct-vida__paso--${p.estado}`} aria-current={p.estado === 'actual' ? 'step' : undefined}>
          <span className="fct-vida__marca">
            {p.estado === 'hecho' && <FctIcono nombre="check" />}
            {p.estado === 'fallido' && <FctIcono nombre="cerrar" />}
          </span>
          {p.nombre}
        </li>
      ))}
    </ol>
  );
}

/* ---------------------------------------------------------------------------
 * Dato — solo lectura se lee como dato (el gris va al marco, nunca al dato).
 * ------------------------------------------------------------------------- */
export interface FctDatoProps {
  etiqueta: string;
  valor?: ReactNode;
  modo?: 'lectura' | 'bloqueado' | 'sin-permiso';
  /** En bloqueado: quién lo edita o por qué. */
  motivo?: string;
  mono?: boolean;
}

export function FctDato({ etiqueta, valor, modo = 'lectura', motivo, mono = false }: FctDatoProps) {
  return (
    <div className="fct-campo">
      <span className="fct-campo__etiqueta">{etiqueta}</span>
      {modo === 'lectura' && <p className={`fct-dato${mono ? ' fct-mono' : ''}`}>{valor ?? '—'}</p>}
      {modo === 'bloqueado' && (
        <p className="fct-dato fct-dato--bloqueado">
          <span className={mono ? 'fct-mono' : undefined}>{valor ?? '—'}</span>
          {motivo && (
            <small>
              <FctIcono nombre="bloqueado" />
              {motivo}
            </small>
          )}
        </p>
      )}
      {modo === 'sin-permiso' && <p className="fct-dato fct-dato--sin-permiso">{motivo ?? 'No tienes permiso para ver este dato'}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Feedback — la única dimensión transitoria.
 * Banda: junto a la causa. Toast: operación en segundo plano. Nunca los dos.
 * ------------------------------------------------------------------------- */
export type FctTipoAviso = 'exito' | 'error' | 'aviso' | 'info' | 'progreso';

const ICONO_AVISO: Record<FctTipoAviso, FctNombreIcono> = {
  exito: 'completado', error: 'alerta', aviso: 'hora', info: 'punto', progreso: 'transmision',
};

export interface FctAvisoProps {
  tipo: FctTipoAviso;
  titulo: string;
  children?: ReactNode;
}

export function FctAviso({ tipo, titulo, children }: FctAvisoProps) {
  return (
    <div className={`fct-aviso fct-aviso--${tipo}`} role={tipo === 'error' ? 'alert' : 'status'}>
      <FctIcono nombre={ICONO_AVISO[tipo]} />
      <span>
        <b>{titulo}</b> {children}
      </span>
    </div>
  );
}

export interface FctToastProps {
  tipo?: 'exito' | 'error';
  mensaje: string;
  onCerrar: () => void;
  /** El éxito se cierra a los 4 s. El error no se cierra solo. */
  duracion?: number | null;
}

export function FctToast({ tipo = 'exito', mensaje, onCerrar, duracion }: FctToastProps) {
  const ms = duracion === undefined ? (tipo === 'exito' ? 4000 : null) : duracion;
  useEffect(() => {
    if (ms == null) return;
    const id = setTimeout(onCerrar, ms);
    return () => clearTimeout(id);
  }, [ms, onCerrar]);
  return (
    <div className={`fct-toast${tipo === 'error' ? ' fct-toast--error' : ''}`} role={tipo === 'error' ? 'alert' : 'status'}>
      <FctIcono nombre={tipo === 'error' ? 'alerta' : 'completado'} />
      <span>{mensaje}</span>
      {tipo === 'error' && (
        <button type="button" onClick={onCerrar} aria-label="Cerrar" style={{ marginLeft: 'auto', background: 'none', border: 0, color: 'inherit', cursor: 'pointer' }}>
          ✕
        </button>
      )}
    </div>
  );
}
